import * as React from "react";
import { Button } from "../button";
import { Icon } from "../icon";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../sheet";

/**
 * Data-scope actions — the reusable "this page is a dashboard over its database"
 * affordance (UI-UX-PATTERNS-PRD Issue 20).
 *
 * Every entity-backed page carries, inline with its title (a `PageHeader
 * toolbar` slot), two controls scoped to *this page's* entity:
 *
 *  - **Import** — invoke the Universal-Intake / typed-import flow into this
 *    entity, without a detour to `/database/import`.
 *  - **Raw data** — open the object/table browser filtered to this entity's
 *    kinds (the same `DataTable` the `/database/objects` inventory uses),
 *    without a detour to `/database/objects`.
 *
 * Presentational + dependency-clean: this block owns only the two toolbar
 * buttons and their overlay Sheets. The actual import + raw-data UI lives in the
 * app layer (the app-local `DataImportClient` + `ObjectsIndexClient`, which
 * import `@excellent/core`), so callers pass that content in via `import` /
 * `rawData` render slots. One pattern, wired per entity — add a page to the
 * model by mounting this in its `PageHeader toolbar`, not by rebuilding a table.
 *
 * The `/database/*` pages remain the global/admin surface; this is the in-scope
 * cut of the same components.
 */
export interface DataScopeActionsProps {
  /**
   * Human label for the entity — e.g. "Finance", "Candidates". Used in the
   * overlay titles ("Import into Finance", "Finance — raw data") and the
   * default button tooltips.
   */
  entityLabel: string;
  /**
   * The import UI to render inside the Import overlay — typically the app-local
   * `<DataImportClient targetKind={…} />`. Omit to hide the Import action (a
   * page with no typed-import target).
   */
  import?: React.ReactNode;
  /** One-line copy under the Import overlay title. */
  importDescription?: React.ReactNode;
  /**
   * The raw-data UI to render inside the Raw-data overlay — typically the
   * app-local `<ObjectsIndexClient scopeKinds={…} chromeless />`. Omit to hide
   * the Raw data action.
   */
  rawData?: React.ReactNode;
  /** One-line copy under the Raw-data overlay title. */
  rawDataDescription?: React.ReactNode;
  className?: string;
}

export function DataScopeActions({
  entityLabel,
  import: importContent,
  importDescription,
  rawData,
  rawDataDescription,
  className,
}: DataScopeActionsProps) {
  const [importOpen, setImportOpen] = React.useState(false);
  const [rawOpen, setRawOpen] = React.useState(false);

  if (!importContent && !rawData) return null;

  return (
    <div className={className} style={{ display: "contents" }}>
      {importContent ? (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => setImportOpen(true)}
            title={`Import into ${entityLabel}`}
          >
            <Icon name="upload" />
            Import
          </Button>
          <Sheet open={importOpen} onOpenChange={setImportOpen}>
            <SheetContent className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl">
              <SheetHeader>
                <SheetTitle>Import into {entityLabel}</SheetTitle>
                <SheetDescription>
                  {importDescription ??
                    `Upload a CSV, TSV, or JSON — rows land straight into ${entityLabel} via the Schema.`}
                </SheetDescription>
              </SheetHeader>
              <div className="px-4 pb-6">{importContent}</div>
            </SheetContent>
          </Sheet>
        </>
      ) : null}

      {rawData ? (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => setRawOpen(true)}
            title={`${entityLabel} — raw data`}
          >
            <Icon name="table" />
            Raw data
          </Button>
          <Sheet open={rawOpen} onOpenChange={setRawOpen}>
            <SheetContent className="w-full gap-0 overflow-y-auto p-0 sm:max-w-3xl">
              <SheetHeader>
                <SheetTitle>{entityLabel} — raw data</SheetTitle>
                <SheetDescription>
                  {rawDataDescription ??
                    `The underlying tables behind ${entityLabel}, scoped to this page.`}
                </SheetDescription>
              </SheetHeader>
              <div className="px-4 pb-6">{rawData}</div>
            </SheetContent>
          </Sheet>
        </>
      ) : null}
    </div>
  );
}
